import { motion } from 'framer-motion';
import { ArrowLeft, Hash, RefreshCw } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useHashtagFeed } from '../hooks/useHashtagFeed';
import { useNostrProfile } from '../hooks/useNostrProfile';
import { useState } from 'react';
import { useReplies } from '../hooks/useReplies';
import { useReactions } from '../hooks/useReactions';
import { nip19 } from 'nostr-tools';
import { useAdmin } from '../contexts/AdminContext';
import { UserAvatar } from '../components/Common/UserAvatar';
import { FeedMedia } from '../components/Feed/FeedMedia';
import { ReplyModal } from '../components/Reply/ReplyModal';
import { ZapModal } from '../components/Zap/ZapModal';
import { useAuth } from '../contexts/AuthContext';
import { reactToEvent } from '../lib/nostr';
import { MessageCircle, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';

// Reuse FeedItem from FlowPage (same component)
function FeedItem({ event }: { event: any }) {
    const { metadata } = useNostrProfile(event.pubkey);
    const [showReplies, setShowReplies] = useState(false);
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
    const [isZapModalOpen, setIsZapModalOpen] = useState(false);
    const { sk, pubkey: currentUserPubkey } = useAuth();
    const { replies, isLoading: loadingReplies } = useReplies(showReplies ? event.id : null);
    const { upvotes, userVotes } = useReactions(event.id);

    const imageRegex = /(https?:\/\/[^\s]+?\.(?:jpg|jpeg|png|webp|gif|svg|avif))/gi;
    const images = event.content.match(imageRegex) || [];
    const contentWithoutImages = event.content.replace(imageRegex, '').trim();

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return 'az önce';
        if (minutes < 60) return `${minutes}d`;
        if (hours < 24) return `${hours}s`;
        if (days < 7) return `${days}g`;
        return date.toLocaleDateString('tr-TR');
    };

    const { settings } = useAdmin();
    if (settings.deletedEventIds.includes(event.id)) return null;
    if (settings.bannedWords.some(word => event.content.toLowerCase().includes(word.toLowerCase()))) return null;

    const npub = nip19.npubEncode(event.pubkey);
    const replyTo = event.tags.find((t: any[]) => t[0] === 'p')?.[1];
    const { metadata: replyToMetadata } = useNostrProfile(replyTo || null);
    const [isProcessingLike, setIsProcessingLike] = useState(false);

    const handleLike = async () => {
        if (!sk && !window.nostr) {
            alert('Beğenmek için giriş yapmalısınız.');
            return;
        }
        if (isProcessingLike) return;

        const currentUserVote = currentUserPubkey ? userVotes[currentUserPubkey]?.content : null;
        if (currentUserVote === '+') return;

        setIsProcessingLike(true);
        try {
            await reactToEvent(event.id, event.pubkey, '+', sk);
        } catch (e) {
            console.error('Like failed:', e);
        } finally {
            setIsProcessingLike(false);
        }
    };

    const isExternal = !event.tags.some((t: any[]) => t[0] === 't' && ['mavililer', 'turkstr', 'turkce'].includes(t[1].toLowerCase()));
    const profileLink = isExternal ? `/u/${npub}` : `/profile/${event.pubkey}`;

    const currentUserVote = currentUserPubkey ? userVotes[currentUserPubkey]?.content : null;
    const isLiked = currentUserVote === '+';

    return (
        <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 hover:bg-surface-50/50 dark:hover:bg-surface-800/30 transition-all overflow-hidden">
            <div className="p-4 md:p-6">
                <div className="flex gap-4">
                    <Link to={profileLink} className="flex-shrink-0">
                        <UserAvatar
                            src={metadata?.picture}
                            name={metadata?.display_name || metadata?.name || event.pubkey}
                            size="lg"
                            isExternal={isExternal}
                        />
                    </Link>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <Link to={profileLink} className="font-poppins font-bold text-gray-900 dark:text-white hover:underline truncate">
                                    {metadata?.display_name || metadata?.name || 'Anonim'}
                                </Link>
                                <span className="text-gray-500 text-sm truncate">
                                    {metadata?.nip05 || `@${npub.slice(0, 8)}...`}
                                </span>
                                <span className="text-gray-400 text-sm flex-shrink-0">·</span>
                                <span className="text-gray-500 text-sm flex-shrink-0">
                                    {formatDate(event.created_at)}
                                </span>
                            </div>
                        </div>

                        {replyTo && (
                            <div className="text-xs text-mavi-500/80 mb-2 font-poppins">
                                yanıtlandı: <span className="font-bold">@{replyToMetadata?.display_name || replyToMetadata?.name || replyTo.slice(0, 8)}</span>
                            </div>
                        )}

                        <div className="font-merriweather text-[0.95rem] leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words mb-3">
                            {contentWithoutImages}
                        </div>

                        <FeedMedia urls={images} />

                        <div className="flex items-center justify-between mt-4 max-w-md text-gray-500 dark:text-gray-400">
                            <button
                                onClick={() => setIsReplyModalOpen(true)}
                                className="flex items-center gap-2 hover:text-mavi-500 transition-colors group"
                                title="Yanıtla"
                            >
                                <div className="p-2 group-hover:bg-mavi-500/10 rounded-full transition-colors">
                                    <MessageCircle className="w-4.5 h-4.5" />
                                </div>
                                <span className="text-sm">Yanıtla</span>
                            </button>

                            <button
                                className={`flex items-center gap-2 transition-colors group ${isLiked ? 'text-pink-500' : 'hover:text-pink-500'}`}
                                onClick={handleLike}
                                title="Beğen"
                            >
                                <div className={`p-2 group-hover:bg-pink-500/10 rounded-full transition-colors ${isLiked ? 'text-pink-500' : ''}`}>
                                    <Heart className={`w-4.5 h-4.5 ${isLiked ? 'fill-current' : ''}`} />
                                </div>
                                {upvotes > 0 && <span className="text-sm">{upvotes}</span>}
                            </button>

                            <button
                                onClick={() => setShowReplies(!showReplies)}
                                className={`flex items-center gap-1 hover:text-mavi-500 transition-colors group ${showReplies ? 'text-mavi-500' : ''}`}
                                title="Yorumları Göster"
                            >
                                <div className="p-2 group-hover:bg-mavi-500/10 rounded-full transition-colors">
                                    {showReplies ? <ChevronUp className="w-4.5 h-4.5" /> : <ChevronDown className="w-4.5 h-4.5" />}
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {showReplies && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-surface-100 dark:border-surface-800 bg-surface-50/30 dark:bg-surface-900/50"
                    >
                        <div className="p-4 space-y-4">
                            {loadingReplies ? (
                                <div className="flex justify-center p-4">
                                    <RefreshCw className="w-5 h-5 animate-spin text-mavi-500" />
                                </div>
                            ) : replies.length > 0 ? (
                                replies.map(reply => (
                                    <div key={reply.id} className="flex gap-3">
                                        <div className="flex-shrink-0 pt-1">
                                            <UserAvatar src={null} name={reply.pubkey.slice(0, 8)} size="sm" isExternal={true} />
                                        </div>
                                        <div className="flex-1 bg-white dark:bg-surface-800 p-3 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-sm">
                                            <p className="text-sm dark:text-gray-200">{reply.content}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-sm text-gray-500 py-4">Henüz yorum yok.</p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ReplyModal
                isOpen={isReplyModalOpen}
                onClose={() => setIsReplyModalOpen(false)}
                replyToEvent={event}
                authorName={metadata?.display_name || metadata?.name || 'Anonim'}
            />
            <ZapModal
                isOpen={isZapModalOpen}
                onClose={() => setIsZapModalOpen(false)}
                targetPubkey={event.pubkey}
                targetName={metadata?.display_name || metadata?.name || 'Anonim'}
                eventId={event.id}
            />
        </div>
    );
}

export function HashtagPage() {
    const { hashtag } = useParams<{ hashtag: string }>();
    const { events, isLoading } = useHashtagFeed(hashtag || null);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto space-y-6"
        >
            {/* Header */}
            <div className="flex items-center gap-4 sticky top-0 bg-surface-50/80 dark:bg-surface-950/80 backdrop-blur-md z-10 py-4">
                <Link
                    to="/flow"
                    className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-mavi-500/10 rounded-xl">
                            <Hash className="w-6 h-6 text-mavi-500" />
                        </div>
                        <div>
                            <h1 className="font-poppins font-bold text-2xl lg:text-3xl text-gray-900 dark:text-white">
                                #{hashtag}
                            </h1>
                            {!isLoading && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {events.length} gönderi
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Feed */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="animate-pulse bg-white dark:bg-surface-900 rounded-2xl p-6 h-48 border border-surface-200 dark:border-surface-800" />
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-sm">
                        <div className="w-20 h-20 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Hash className="w-10 h-10 text-gray-300 dark:text-gray-600" />
                        </div>
                        <h3 className="text-xl font-poppins font-bold text-gray-900 dark:text-white mb-2">
                            Henüz içerik yok
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            #{hashtag} etiketiyle henüz bir gönderi bulamadık.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {events.map((event: any) => (
                            <FeedItem key={event.id} event={event} />
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    );
}
